import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Grid3x3, ChefHat, ClipboardList, CalendarClock, CalendarDays, Users, UtensilsCrossed,
  Activity, BarChart3, CakeSlice, UserCog, KeyRound, Map, Settings as SettingsIcon, Printer,
  ShoppingCart, Lock, X, Bell, Volume2, VolumeX, MessageSquare, LogOut, Send, Palette,
  CreditCard, History, Boxes, Sparkles, Bike, SlidersHorizontal, Wallet, Trash2, DatabaseBackup, FileText, Hourglass, BookOpen, MoreHorizontal, Receipt, Hand, Download,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { TrialBanner } from "@/components/TrialBanner";
import { Questionario, FeedbackButton } from "@/components/Questionario";
import { Orologio } from "@/components/Orologio";
import { TestoGrande } from "@/components/TestoGrande";
import { useI18n, tl } from "@/i18n";
import { cn } from "@/lib/utils";
import { g, p, pu } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MobileNav } from "@/components/MobileNav";
import { InstallPrompt, apriInstalla, installabile } from "@/components/InstallPrompt";
import { PercorsoDemo } from "@/components/demo/PercorsoDemo";
import { VetrinaNastro, VetrinaBlocco, DemoNastro } from "@/components/Vetrina";
import { Benvenuto } from "@/components/Benvenuto";
import { ChiediPiva } from "@/components/ChiediPiva";
import { CambiaPin } from "@/components/CambiaPin";

const SALA = [
  { to: "/sala", labelKey: "nav.sala", icon: Grid3x3, id: "sala", mod: "sala" },
  { to: "/prenotazioni", labelKey: "nav.prenotazioni", icon: CalendarClock, id: "prenotazioni", perm: "can_book" },
  { to: "/ritiri", labelKey: "nav.ritiri", icon: Hand, id: "ritiri" },
  { to: "/asporto", labelKey: "nav.asporto", icon: Bike, id: "asporto", mod: "asporto" },
  { to: "/attesa", labelKey: "nav.attesa", icon: Hourglass, id: "attesa" },
  { to: "/piatti-giorno", labelKey: "nav.piatti-giorno", icon: ChefHat, id: "piatti-giorno" },
  { to: "/manuale", labelKey: "nav.manuale", icon: BookOpen, id: "manuale", noShowcase: true },
  { to: "/cassa", labelKey: "nav.cassa", icon: Wallet, id: "cassa" },
  { to: "/monitor", labelKey: "nav.monitor", icon: ChefHat, id: "monitor", mod: "monitor" },
];
const DIR = [{ to: "/direttore", labelKey: "nav.direttore", icon: ClipboardList, id: "direttore" }];
const TIT = [
  { to: "/", labelKey: "nav.serata", icon: LayoutDashboard, id: "serata" },
  { to: "/clienti", labelKey: "nav.clienti", icon: Users, id: "clienti", mod: "clienti" },
  { to: "/piantina", labelKey: "nav.piantina", icon: Map, id: "piantina", mod: "piantina" },
];
const TIT_MENU = [
  { to: "/menu", labelKey: "nav.menu", icon: UtensilsCrossed, id: "menu", mod: "menu" },
  { to: "/menu-fisso", labelKey: "nav.menu-fisso", icon: ClipboardList, id: "menu-fisso", mod: ["menu_fisso", "carta_qr"] },
  { to: "/dolci-pesce", labelKey: "nav.dolci-pesce", icon: CakeSlice, id: "dolci-pesce", mod: "dolci-pesce" },
  { to: "/delivery-guida", labelKey: "nav.delivery-guida", icon: Bike, id: "delivery-guida", mod: "delivery_guide" },
];
const TIT_PERSONE = [
  { to: "/dipendenti", labelKey: "nav.dipendenti", icon: UserCog, id: "dipendenti", mod: "dipendenti" },
  { to: "/turni", labelKey: "nav.turni", icon: CalendarDays, id: "turni", setting: "shifts_on" },
];
const TIT_SOLDI = [
  { to: "/movimenti", labelKey: "nav.movimenti", icon: Activity, id: "movimenti", mod: "movimenti" },
  { to: "/cassa-fiscale", labelKey: "nav.cassa-fiscale", icon: Receipt, id: "cassa-fiscale", mod: "cassa_fiscale" },
  { to: "/abbonamento", labelKey: "nav.abbonamento", icon: CreditCard, id: "abbonamento" },
  { to: "/caso-studio", labelKey: "nav.caso-studio", icon: FileText, id: "caso-studio" },
];
const PREMIUM = [
  { to: "/storico", labelKey: "nav.storico", icon: History, id: "storico", feature: "order_history", needed: "standard" },
  { to: "/sprechi", labelKey: "nav.sprechi", icon: Trash2, id: "sprechi", feature: "waste", needed: "standard" },
  { to: "/statistiche", labelKey: "nav.statistiche", icon: BarChart3, id: "statistiche", feature: "reports", needed: "pro" },
  { to: "/magazzino", labelKey: "nav.magazzino", icon: Boxes, id: "magazzino", feature: "inventory", needed: "pro" },
  { to: "/backup", labelKey: "nav.backup", icon: DatabaseBackup, id: "backup", feature: "backup", needed: "pro" },
];
const LOCKED = [
  { to: "/stampanti", labelKey: "nav.stampanti", icon: Printer, id: "stampanti" },
  { to: "/spesa", labelKey: "nav.spesa", icon: ShoppingCart, id: "spesa" },
];

const ChatPanel = () => {
  const { user } = useApp();
  const { t } = useI18n();
  const [threads, setThreads] = useState([]);
  const [to, setTo] = useState("sala");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    g("/chat/threads", { me: user.name }).then(setThreads).catch(() => setThreads([]));
  }, [user.name]);

  useEffect(() => {
    const load = () => g("/chat", { to }).then(setMsgs).catch(() => {});
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [to]);

  const send = async () => {
    if (!text.trim()) return;
    await p("/chat", { author: user.name, text, to });
    setText("");
    setMsgs(await g("/chat", { to }));
  };

  const dms = threads.filter((x) => x.kind === "dm");

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap gap-1.5 pb-3" data-testid="chat-threads">
        <button
          data-testid="chat-thread-sala"
          onClick={() => setTo("sala")}
          className={`rounded-full px-3 py-1.5 text-[11px] font-bold ${to === "sala" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
        >
          {t("x.chat.room")}
        </button>
        {dms.map((x) => (
          <button
            key={x.id}
            data-testid={`chat-thread-${x.name.split(" ")[0].toLowerCase()}`}
            onClick={() => setTo(x.id)}
            className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${to === x.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
          >
            {x.name.split(" ")[0]}
          </button>
        ))}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto pr-1" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-sm text-slate-500">{t("x.chat.ph")}…</p>}
        {msgs.map((m) => (
          <div key={m.id} className="rounded-md border border-slate-200 bg-white p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{m.author}</p>
            <p className="mt-1 text-sm">{m.key ? t(m.key, m.params || {}) : m.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("x.chat.ph")} data-testid="chat-input" />
        <Button onClick={send} data-testid="chat-send-btn">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const { settings, user, logout, sound, setSound, notifs, clearNotifs, exitTenant, modules, viewOnly, showcase, demoMode } = useApp();
  const { plan, has, askUpgrade } = usePlan();
  const { t, lang, setLang, langs } = useI18n();
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const navigate = useNavigate();
  const role = user.role;

  // Il titolare salva la lingua del locale, gli altri la cambiano solo sul proprio dispositivo.
  const saveLang = async (id) => {
    setLang(id);
    if (role !== "titolare") return;
    try {
      await pu("/settings", { ...settings, lang: id });
      toast.success(tl(id, "nav.lang.saved"));
    } catch {
      /* la lingua resta almeno su questo dispositivo */
    }
  };

  const langSwitch = (size = "sm") => (
    <div className="flex items-center gap-0.5 rounded-full border border-slate-200 p-0.5" data-testid="app-lang-switch">
      {langs.map((l) => (
        <button
          key={l.id}
          data-testid={`app-lang-${l.id}`}
          onClick={() => saveLang(l.id)}
          className={cn("rounded-full font-bold transition-colors",
            size === "lg" ? "px-3 py-2 text-xs" : "px-2 py-1 text-[10px]",
            lang === l.id ? "bg-slate-900 text-white" : "text-slate-400 hover:text-slate-700")}
        >
          {l.flag}
        </button>
      ))}
    </div>
  );

  // I moduli spenti in "Funzioni attive" scompaiono dal menu (serata e abbonamento restano sempre).
  const enabled = (list) =>
    (modules
      ? list.filter((n) => !n.mod || (Array.isArray(n.mod) ? n.mod.some((m) => modules.includes(m)) : modules.includes(n.mod)))
      : list
    ).filter((n) => !n.setting || settings?.[n.setting])
      .filter((n) => !n.perm || user[n.perm] !== false)
      // Il manuale d'uso resta a chi si registra: nelle vetrine d'esempio non compare.
      .filter((n) => !n.noShowcase || !showcase);

  const groups = [];
  if (user.can_send === false) groups.push([t("nav.group.dept"), [{ to: "/monitor", labelKey: "nav.monitor", icon: ChefHat, id: "monitor" }]]);
  else groups.push([t("nav.group.sala"), enabled(SALA)]);
  if (settings?.shifts_on && user.can_send !== false && role === "cameriere")
    groups.push(["Turni", enabled([TIT_PERSONE.find((n) => n.id === "turni")].filter(Boolean))]);
  if (role === "direttore" || role === "titolare") groups.push([t("nav.group.dir"), DIR]);
  if (role === "titolare") {
    groups.push([t("nav.group.owner"), enabled(TIT)]);
    groups.push(["Menu e prodotti", enabled(TIT_MENU)]);
    groups.push(["Persone", enabled(TIT_PERSONE)]);
    groups.push(["Soldi e documenti", enabled(TIT_SOLDI)]);
  }

  // Le 4 voci della barra in basso cambiano con il ruolo: sempre pagine attive per quel profilo.
  const mobileNav = (() => {
    const byId = (list, id) => list.find((n) => n.id === id);
    const sala = enabled(SALA);
    if (user.can_send === false) {
      return [
        { to: "/monitor", labelKey: "nav.monitor", icon: ChefHat, id: "monitor" },
        ...(showcase ? [] : [{ to: "/manuale", labelKey: "nav.manuale", icon: BookOpen, id: "manuale" }]),
        { to: "/guida", labelKey: "nav.guida", icon: Sparkles, id: "guida" },
      ];
    }
    const base = role === "titolare"
      ? [byId(TIT, "serata"), byId(sala, "sala"), byId(sala, "cassa"), byId(TIT_MENU, "menu")]
      : role === "direttore"
        ? [byId(DIR, "direttore"), byId(sala, "sala"), byId(sala, "cassa"), byId(sala, "monitor")]
        : [byId(sala, "sala"), byId(sala, "asporto"), byId(sala, "cassa"), byId(sala, "attesa"), byId(sala, "monitor")];
    return base.filter(Boolean).slice(0, 4);
  })();

  const item = (n) => (    <NavLink
      key={n.id}
      to={n.to}
      end={n.to === "/"}
      onClick={() => setOpen(false)}
      data-testid={`nav-${n.id}`}
      className={({ isActive }) =>
        cn("flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
          isActive ? "bg-white text-slate-900" : "text-slate-300 hover:bg-slate-800 hover:text-white")
      }
    >
      <n.icon className="h-4 w-4 shrink-0" />
      {t(n.labelKey)}
    </NavLink>
  );

  const sidebar = (
    <div className="flex min-h-full flex-col gap-6 bg-slate-900 px-4 py-6 pb-10">
      <div className="px-2">
        {settings.logo && (
          <img src={settings.logo} alt="" data-testid="sidebar-logo"
               className="mb-3 h-14 w-14 rounded-lg bg-white object-contain p-1" />
        )}
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("nav.overline")}</p>
        <h2 className="mt-1 text-lg font-bold leading-tight text-white" data-testid="sidebar-business-name">
          {settings.business_name}
        </h2>
        <p className="mt-0.5 text-[11px] font-semibold tracking-wide text-amber-400" data-testid="sidebar-product-name">
          Reprehendo <span className="text-slate-500">(RH)</span>
        </p>
        <p className="mt-2 text-xs text-slate-400">
          {user.name} · {user.role_label || user.role}
        </p>
      </div>

      {groups.map(([title, items]) => (
        <nav key={title} className="flex flex-col gap-1">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{title}</p>
          {items.map(item)}
        </nav>
      ))}

      {role === "titolare" && (
        <nav className="flex flex-col gap-1">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Funzioni premium
          </p>
          {PREMIUM.map((n) =>
            has(n.feature) ? (
              item(n)
            ) : (
              <button
                key={n.id}
                data-testid={`nav-premium-${n.id}`}
                onClick={() => {
                  setOpen(false);
                  navigate(n.to);
                }}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-800/60"
              >
                <n.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{t(n.labelKey)}</span>
                <span className="rounded bg-amber-400/90 px-1.5 text-[9px] font-bold uppercase text-slate-900">
                  {n.needed === "pro" ? "Pro" : "Std"}
                </span>
              </button>
            )
          )}
        </nav>
      )}

      {role === "titolare" && (
        <nav className="flex flex-col gap-1">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Bloccate nella demo</p>
          {LOCKED.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-locked-${n.id}`}
              onClick={() => {
                toast.info(`${t(n.labelKey)} non è utilizzabile nella demo`, {
                  description: "Puoi vedere l'anteprima. Attiva nella versione completa.",
                });
                setOpen(false);
                navigate(n.to);
              }}
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-800/60"
            >
              <n.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{t(n.labelKey)}</span>
              <Lock className="h-3.5 w-3.5" />
            </button>
          ))}
        </nav>
      )}

      <div className="mt-auto flex flex-col gap-1">
        {item({ to: "/guida", labelKey: "nav.guida", icon: Sparkles, id: "guida" })}
        {installabile() && (
          <button
            onClick={() => { setOpen(false); apriInstalla(); }}
            data-testid="nav-installa-btn"
            className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
          >
            <Download className="h-4 w-4 shrink-0" /> {t("nav.installa")}
          </button>
        )}
        {item({ to: "/colori", labelKey: "nav.colori", icon: Palette, id: "colori" })}
        {role === "titolare" && item({ to: "/funzioni", labelKey: "nav.funzioni", icon: SlidersHorizontal, id: "funzioni" })}
        {role === "titolare" && item({ to: "/impostazioni", labelKey: "nav.impostazioni", icon: SettingsIcon, id: "impostazioni" })}
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> {t("nav.changeRole")}
        </button>
        <button
          onClick={exitTenant}
          data-testid="exit-tenant-btn"
          className="flex items-center gap-3 rounded-md px-3 py-2.5 text-xs font-medium text-slate-500 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-3.5 w-3.5" /> {t("nav.exit")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Questionario open={feedback} onOpenChange={setFeedback} business={settings.business_name} role={user.role} />

      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 overflow-y-auto lg:block">{sidebar}</aside>

        {open && (
          <div className="fixed inset-0 z-[60] lg:hidden">
            <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto overscroll-contain bg-slate-900 pb-[env(safe-area-inset-bottom)] shadow-2xl">
              <button onClick={() => setOpen(false)} data-testid="close-sidebar-btn" className="absolute right-3 top-3 z-10 rounded-md p-2.5 text-slate-400">
                <X className="h-5 w-5" />
              </button>
              {sidebar}
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1">
          {/* Un solo blocco fisso in alto: strisce e intestazione non si sovrappongono più */}
          <div className="sticky top-0 z-40 pt-[env(safe-area-inset-top)]">
            {viewOnly && <VetrinaNastro />}
            {demoMode && !viewOnly && <DemoNastro />}
            {!demoMode && (
            <div className="flex items-center gap-2 bg-slate-900 px-3 py-1.5 text-white sm:gap-3 sm:px-6 sm:py-2" data-testid="demo-banner">
              <Lock className="hidden h-3.5 w-3.5 shrink-0 text-amber-400 sm:block" />
              <p className="min-w-0 flex-1 truncate text-xs sm:text-sm">
                <span className="rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-900">Beta</span>
                <span className="ml-2 font-bold">{t("beta.tag")}</span>
                <span className="hidden sm:inline"> {t("beta.long")}</span>
              </p>
              <button
                data-testid="beta-feedback-btn"
                onClick={() => setFeedback(true)}
                className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold hover:bg-white/20"
              >
                {t("beta.cta")}
              </button>
            </div>
            )}
            <TrialBanner owner={role === "titolare"} />

            <header className="flex items-center gap-1 border-b border-slate-200 bg-white px-2 py-2 sm:gap-2 sm:px-8 sm:py-3">
              {/* Su telefono il menu si apre da «Altro» in basso: qui non serve un secondo pulsante */}
              {settings.logo && (
                <img src={settings.logo} alt="" data-testid="header-logo"
                     className="h-9 w-9 shrink-0 rounded-md bg-white object-contain sm:h-10 sm:w-10" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">
                  {settings.business_name}
                  <span className="ml-2 hidden text-[11px] font-medium text-slate-400 sm:inline">Reprehendo (RH)</span>
                </p>
                <p className="truncate text-xs text-slate-500">{user.name} · {user.role_label || user.role}</p>
              </div>

              <Orologio />

              <TestoGrande />

              <span className="hidden rounded-full bg-amber-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800 sm:inline-block" data-testid="header-plan-badge">
                {plan?.plan_name || "Gratuito"}
              </span>

              {role === "titolare" && plan?.plan !== "pro" && (
                <button
                  data-testid="header-upgrade-btn"
                  onClick={() => askUpgrade("upgrade", t("nav.upgrade"), plan?.plan === "free" ? "standard" : "pro")}
                  className="hidden items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 sm:flex"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Upgrade
                </button>
              )}

              <div className="hidden sm:block">{langSwitch()}</div>

              <button onClick={() => setSound(!sound)} data-testid="sound-toggle-btn" className="hidden rounded-md p-2.5 hover:bg-slate-100 sm:block" title={t("nav.sound")}>
                {sound ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5 text-slate-400" />}
              </button>

              {/* Sul telefono piano, lingua e audio stanno in un unico menu: la barra resta leggibile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="header-more-btn" className="shrink-0 rounded-md p-2.5 hover:bg-slate-100 sm:hidden" title={t("nav.more")}>
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 p-3" data-testid="header-more-menu">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">{t("nav.plan")}</span>
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800" data-testid="more-plan-badge">
                      {plan?.plan_name || "Gratuito"}
                    </span>
                  </div>
                  {role === "titolare" && plan?.plan !== "pro" && (
                    <button
                      data-testid="more-upgrade-btn"
                      onClick={() => askUpgrade("upgrade", t("nav.upgrade"), plan?.plan === "free" ? "standard" : "pro")}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md bg-slate-900 px-3 py-2.5 text-xs font-semibold text-white"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Upgrade
                    </button>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">{t("nav.sound")}</span>
                    <button onClick={() => setSound(!sound)} data-testid="more-sound-btn" className="rounded-md p-2 hover:bg-slate-100">
                      {sound ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5 text-slate-400" />}
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-500">{t("nav.lang")}</span>
                    {langSwitch("lg")}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="chat-open-btn" className="shrink-0 rounded-md p-2.5 hover:bg-slate-100" title={t("nav.chat")}>
                  <MessageSquare className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent className="flex flex-col" data-testid="chat-sheet">
                <SheetHeader>
                  <SheetTitle>{t("nav.chat")}</SheetTitle>
                </SheetHeader>
                <div className="mt-4 flex-1 overflow-hidden">
                  <ChatPanel />
                </div>
              </SheetContent>
            </Sheet>

            <Sheet onOpenChange={(v) => !v && notifs.length && clearNotifs()}>
              <SheetTrigger asChild>
                <button data-testid="notifications-btn" className="relative shrink-0 rounded-md p-2.5 hover:bg-slate-100" title={t("nav.alerts")}>
                  <Bell className="h-5 w-5" />
                  {notifs.length > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
                      {notifs.length}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent data-testid="notifications-sheet">
                <SheetHeader>
                  <SheetTitle>{t("nav.alerts")}</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-3">
                  {notifs.length === 0 && <p className="text-sm text-slate-500">{t("n.empty")}</p>}
                  {notifs.map((n) => {
                    const mins = Math.floor((Date.now() - new Date(n.at).getTime()) / 60000);
                    const claimable = n.kind === "pronto";
                    return (
                      <div key={n.id} className="rounded-md border border-slate-200 p-3 text-sm" data-testid={`notif-${n.id}`}>
                        {n.key ? t(n.key, n.params || {}) : n.text}
                        {claimable && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {n.claimed_by ? (
                              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800" data-testid={`claimed-${n.id}`}>
                                {t("x.claim.going", { name: n.claimed_by })}
                              </span>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  data-testid={`claim-${n.id}`}
                                  onClick={async () => {
                                    try {
                                      await p(`/notifications/${n.id}/claim`, { by: user.name });
                                      toast.success(t("x.claim.going", { name: user.name }));
                                    } catch (e) {
                                      toast.error(e?.response?.data?.detail || "");
                                    }
                                  }}
                                >
                                  {t("x.claim.go")}
                                </Button>
                                {mins >= 3 && (
                                  <span className="text-[11px] font-semibold text-rose-600" data-testid={`help-${n.id}`}>
                                    {t("x.claim.help")}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {notifs.length > 0 && (
                    <Button variant="outline" className="w-full" data-testid="clear-notifications-btn" onClick={clearNotifs}>
                      Segna come letti
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
            </header>
          </div>
          <div className="px-4 py-6 pb-[calc(9.5rem+env(safe-area-inset-bottom))] sm:px-8 lg:pb-6">{children}</div>
        </main>
      </div>
      <MobileNav items={mobileNav} onMore={() => setOpen(true)} />
      <InstallPrompt />
      <PercorsoDemo />
      <FeedbackButton business={settings.business_name} role={user.role} />
      <VetrinaBlocco />
      <Benvenuto />
      <ChiediPiva />
      <CambiaPin />
    </div>
  );
};
